<template>
  <div class="container mt-4">
    <h3 class="text-center mb-4">Activate Rounds</h3>
    <button @click="inactivateAllRounds" class="btn btn-warning mb-3">
      Inactivate All Rounds
    </button>
    <table class="table table-striped mt-4">
      <thead>
        <tr>
          <th>Round Number</th>
          <th>Round Name</th>
          <th>Status</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="round in sortedRounds" :key="round.roundId">
          <td>{{ round.roundNumber }}</td>
          <td>{{ round.roundName }}</td>
          <td>{{ round.isActive ? 'Active' : 'Inactive' }}</td>
          <td>
            <button
              v-if="!round.isActive"
              @click="activateRound(round.roundId)"
              class="btn btn-success btn-sm"
            >
              Activate
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { collection, query, where, getDocs, doc, writeBatch } from 'firebase/firestore';
import { db } from '@/firebase';

export default {
  name: 'ActivateRounds',
  props: {
    selectedLocation: String, // Receive the selected location as a prop from HostPage
  },
  setup(props) {
    const rounds = ref([]);
    const gameId = ref('');

    const getTodayDate = () => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const fetchRounds = async () => {
      try {
        const gamesQuery = query(
          collection(db, 'games'),
          where('location', '==', props.selectedLocation),
          where('date', '==', getTodayDate())
        );
        const gamesSnapshot = await getDocs(gamesQuery);

        if (!gamesSnapshot.empty) {
          const gameDoc = gamesSnapshot.docs[0];
          gameId.value = gameDoc.id;

          const roundsCollectionRef = collection(db, `games/${gameId.value}/rounds`);
          const roundsSnapshot = await getDocs(roundsCollectionRef);

          rounds.value = roundsSnapshot.docs.map(doc => ({
            roundId: doc.id,
            roundNumber: parseInt(doc.id.replace('round', '')), // Extracting the round number from the ID
            ...doc.data(),
          }));
        } else {
          alert('No game found for the current location and date.');
        }
      } catch (error) {
        console.error('Error fetching rounds:', error);
      }
    };

    const activateRound = async (roundId) => {
      try {
        const batch = writeBatch(db); // Initialize batch operation

        // Deactivate all rounds
        rounds.value.forEach((round) => {
          const roundDocRef = doc(db, `games/${gameId.value}/rounds`, round.roundId);
          batch.update(roundDocRef, { isActive: false });
        });

        // Activate the selected round
        const selectedRoundDocRef = doc(db, `games/${gameId.value}/rounds`, roundId);
        batch.update(selectedRoundDocRef, { isActive: true });

        await batch.commit(); // Commit the batch operation

        // Update the local rounds array to reflect the changes
        rounds.value.forEach((round) => {
          round.isActive = round.roundId === roundId;
        });

        alert('Round activated successfully!');
      } catch (error) {
        console.error('Error activating round:', error);
        alert('There was an error activating the round. Please try again.');
      }
    };

    const inactivateAllRounds = async () => {
      try {
        const batch = writeBatch(db); // Initialize batch operation

        // Inactivate all rounds
        rounds.value.forEach((round) => {
          const roundDocRef = doc(db, `games/${gameId.value}/rounds`, round.roundId);
          batch.update(roundDocRef, { isActive: false });
        });

        await batch.commit(); // Commit the batch operation

        // Update the local rounds array to reflect the changes
        rounds.value.forEach((round) => {
          round.isActive = false;
        });

        alert('All rounds have been inactivated successfully!');
      } catch (error) {
        console.error('Error inactivating rounds:', error);
        alert('There was an error inactivating the rounds. Please try again.');
      }
    };

    onMounted(fetchRounds);

    const sortedRounds = computed(() => {
      // Return a new sorted array without modifying the original rounds array
      return [...rounds.value].sort((a, b) => a.roundNumber - b.roundNumber);
    });

    return {
      rounds,
      sortedRounds,
      activateRound,
      inactivateAllRounds,
    };
  },
};
</script>


<style scoped>
/* Add any specific styles here */
</style>
